// src/components/Cash/CashDrawerAdminView.jsx
import { useEffect, useMemo, useRef, useState } from 'react';
import {
  collection, query, where, orderBy, onSnapshot,
  addDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../../firebase';
import { closeCashRegister, openAssignedCashRegister } from '../../lib/cashApi';
import { calculateTurnSalesAccounting } from '../../lib/cashSalesAccounting';

const fmtCOP = (n) =>
  Number(n || 0).toLocaleString('es-CO', { style: 'currency', currency: 'COP', maximumFractionDigits: 0 });

// Firestore conserva registros históricos con esquemas distintos. Nunca se
// debe entregar un objeto directamente a React porque tumba todo el modal.
const safeDisplayText = (value, fallback = '') => {
  if (typeof value === 'string') return value;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (value?.toDate instanceof Function) {
    try { return value.toDate().toLocaleString('es-CO'); } catch (_error) { return fallback; }
  }
  return fallback;
};

const isWaiterRole = (role = '') => {
  const normalized = String(role || '').toLowerCase();
  return ['mesero', 'mesera', 'waiter'].includes(normalized);
};

export default function CashDrawerAdminView({ onBack, currentUser }) {
  const [users, setUsers] = useState([]);
  const [openDrawers, setOpenDrawers] = useState({}); // { [email]: { id, ...caja } }
  const [openDrawerRows, setOpenDrawerRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openingUser, setOpeningUser] = useState(null);
  const [search, setSearch] = useState('');
  const [openAmount, setOpenAmount] = useState({});   // { [uid]: number }

// UI
const [viewMode, setViewMode] = useState('activos'); // 'activos' | 'historial'
const [selectedWaiter, setSelectedWaiter] = useState(null);

// Historial
const [history, setHistory] = useState([]); // turnos cerrados del mesero seleccionado
const [lastClosed, setLastClosed] = useState(null); // último turno cerrado del mesero


  // Métricas vivas por turno: { [turnId]: { salesCount, salesTotal, cashIn, change, abonos, retiros, netCash, lastSales:[...] } }
  const [metrics, setMetrics] = useState({});
  const gastoStoreRef = useRef({ turn: {}, related: {} });

  // Modal de ajuste (abono / retiro)
  const [adjust, setAdjust] = useState({ open:false, type:'abono', uid:null, email:'', value:'', note:'' });
  const [adjustLoading, setAdjustLoading] = useState(false);

  // ▼ nuevo estado para modal de cierre
const [closing, setClosing] = useState({ open:false, uid:null, email:'', loading:false, error:'', finalAmount:'' });

// Toast/Modal ligero
  const [toast, setToast] = useState({
  open: false,
  type: 'success',        // 'success' | 'error' | 'info'
  title: '',
  message: '',
  autoCloseMs: 2500,
});
  const [responsibleDrawerEmail, setResponsibleDrawerEmail] = useState(currentUser?.email || '');
  const [adminClosingId, setAdminClosingId] = useState(null);
  const [adminCloseModal, setAdminCloseModal] = useState({
    open: false,
    drawer: null,
    ownerLabel: '',
    ownerEmail: '',
    finalAmount: '',
    reason: '',
    estimatedCash: 0,
    baseAmount: 0,
    salesTotal: 0,
    error: '',
  });
  const currentRole = String(currentUser?.role || '').trim().toLowerCase();
  const canAdministrativelyClose = ['ti', 'administrador', 'admin'].includes(currentRole);

  useEffect(() => {
    if (!adminCloseModal.open) return undefined;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const handleKeyDown = (event) => {
      if (event.key === 'Escape' && !adminClosingId) {
        setAdminCloseModal((current) => ({ ...current, open: false, error: '' }));
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [adminCloseModal.open, adminClosingId]);

  const assignedDrawerEmail = responsibleDrawerEmail || currentUser?.email || '';
  const assignedDrawer =
    assignedDrawerEmail && openDrawers[assignedDrawerEmail]
      ? openDrawers[assignedDrawerEmail]
      : openDrawers[currentUser?.email] || null;
  const assignedDrawerMetrics = assignedDrawer ? (metrics[assignedDrawer.id] || {}) : {};
  const assignedDrawerAvailableCash = assignedDrawer
    ? Number(assignedDrawer.montoApertura || 0)
      + Number(assignedDrawerMetrics.abonos || 0)
      + Number(assignedDrawerMetrics.returnTotal || 0)
      + (Number(assignedDrawerMetrics.cashIn || 0) - Number(assignedDrawerMetrics.change || 0) - Number(assignedDrawerMetrics.cashRefunds || 0))
      - Number(assignedDrawerMetrics.retiros || 0)
      - Number(assignedDrawerMetrics.expenseTotal || 0)
    : 0;

  const openDrawerOptions = useMemo(() => {
    return Object.keys(openDrawers).map(email => {
      const user = users.find(u => u.email === email);
      return {
        email,
        label: user?.displayName || email,
        amount: Number(openDrawers[email]?.montoApertura || 0),
      };
    });
  }, [openDrawers, users]);

  useEffect(() => {
    if (currentUser?.role === 'Cajero' && currentUser?.email && openDrawers[currentUser.email]) {
      if (responsibleDrawerEmail !== currentUser.email) {
        setResponsibleDrawerEmail(currentUser.email);
      }
      return;
    }

    if (!responsibleDrawerEmail) {
      const fallback = Object.keys(openDrawers)[0] || '';
      if (fallback) setResponsibleDrawerEmail(fallback);
      return;
    }

    if (responsibleDrawerEmail && !openDrawers[responsibleDrawerEmail]) {
      const fallback = Object.keys(openDrawers)[0] || '';
      if (fallback && fallback !== responsibleDrawerEmail) {
        setResponsibleDrawerEmail(fallback);
      }
    }
  }, [openDrawers, currentUser?.role, currentUser?.email, responsibleDrawerEmail]);

function showToast({ type='success', title='', message='', autoCloseMs=2500 }) {
  setToast({ open:true, type, title, message, autoCloseMs });
  if (autoCloseMs > 0) {
    setTimeout(() => setToast(t => ({ ...t, open:false })), autoCloseMs);
  }
}


// arma el reporte a partir de openDrawers + metrics
  const buildCloseReport = (u) => {
  const abierta = openDrawers[u.email];
  if (!abierta) return null;
  const m = metrics[abierta.id] || {};
  const efectivoNeto = Number(m.cashIn||0) - Number(m.change||0) - Number(m.cashRefunds || 0);
  const totalAbonos = Number(m.abonos || 0);
  const movements = m.movements || [];
  const advanceTotal = movements.filter(x => x.isAdvance).reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const returnTotal = movements.filter(x => !x.isAdvance && x.direction === 'in').reduce((sum, item) => sum + Number(item.amount || 0), 0);
  const cajaFinal =
    Number(abierta.montoApertura||0) +
    totalAbonos +
    returnTotal +
    efectivoNeto -
    Number(m.retiros || 0) -
    Number(m.expenseTotal || 0);
  return {
    turnId: abierta.id,
    fecha: abierta.fecha,
    hora: abierta.hora,
    base: Number(abierta.montoApertura||0),
    abonos: totalAbonos,
    retiros: Number(m.retiros||0),
    gastos: Number(m.expenseTotal||0),
    ventasCount: Number(m.salesCount||0),
    ventasTotal: Number(m.salesTotal||0),
    efectivoRecibido: Number(m.cashIn||0),
    cambio: Number(m.change||0),
    efectivoNeto,
    cajaFinal,
    methods: m.methodTotals || {},
    lastSales: m.lastSales || [],
    movements,
    advanceTotal,
    returnTotal
  };
};

const printCloseReport = (u, rep) => {
  const fmt = (n) => Number(n||0).toLocaleString('es-CO',{style:'currency',currency:'COP',maximumFractionDigits:0});
  const lines = [
    '==============================',
    'REPORTE DE CIERRE DE CAJA',
    '==============================',
    `Usuario: ${u.displayName || u.email}`,
    `Fecha:   ${rep.fecha || ''}  Hora: ${rep.hora || ''}`,
    `Turno:   ${rep.turnId}`,
    '------------------------------',
    `Base inicial:  ${fmt(rep.base)}`,
    `Abonos:        ${fmt(rep.abonos)}`,
    `Retiros:       ${fmt(rep.retiros)}`,
    `Gastos:         ${fmt(rep.gastos)}`,
    '------------------------------',
    `# Ventas:      ${rep.ventasCount}`,
    `Total ventas:  ${fmt(rep.ventasTotal)}`,
    `Efectivo rec.: ${fmt(rep.efectivoRecibido)}`,
    `Cambio dado:   ${fmt(rep.cambio)}`,
    '------------------------------',
    'Recaudo por mǸtodo:'
  ];
  Object.entries(rep.methods||{}).forEach(([k,v])=>lines.push(`  - ${k}: ${fmt(v)}`));
  lines.push('------------------------------');

  if (typeof rep.advanceTotal === 'number' || typeof rep.returnTotal === 'number') {
    lines.push('Movimientos clave:');
    if (typeof rep.advanceTotal === 'number') {
      lines.push(`  - Avances entregados: ${fmt(rep.advanceTotal)}`);
    }
    if (typeof rep.returnTotal === 'number') {
      lines.push(`  - Dinero recibido de vuelta: ${fmt(rep.returnTotal)}`);
    }
    lines.push('------------------------------');
  }

  if (rep.movements?.length) {
    lines.push('Movimientos de caja:');
    rep.movements.forEach(m => {
      const label = m.description || m.category || 'Movimiento';
      const user = m.relatedUser ? ` (${m.relatedUser})` : '';
      const prefix = m.isAdvance ? 'Avance' : 'Retiro';
      lines.push(`  - ${prefix}: ${label}${user} → ${fmt(m.amount)}`);
    });
    lines.push('------------------------------');
  }

  lines.push(`Efectivo esperado: ${fmt(rep.cajaFinal)}`);
  if (closing.finalAmount !== '') {
    const difference = Number(closing.finalAmount) - rep.cajaFinal;
    lines.push(`Efectivo contado: ${fmt(closing.finalAmount)}`);
    lines.push(`${difference === 0 ? 'Sin diferencia' : difference > 0 ? 'Sobrante' : 'Faltante'}: ${fmt(Math.abs(difference))}`);
  }
  lines.push('Vista previa. El cierre se confirma en el sistema.', '==============================');

  const w = window.open('', '_blank');
  w.document.write('<pre style="font:14px monospace;white-space:pre-wrap">'+lines.join('\n')+'</pre>');
  w.document.close();
  w.print();
};

  const openCloseModal = (u) => setClosing({ open:true, uid:u.uid, email:u.email, loading:false, error:'', finalAmount:'' });
  const closeCloseModal = () => {
    if (closing.loading) return;
    setClosing({ open:false, uid:null, email:'', loading:false, error:'', finalAmount:'' });
  };

  const confirmCloseWithTotals = async () => {
    if (closing.loading) return;
    const u = users.find(x => x.uid === closing.uid);
    if (!u) return closeCloseModal();
    const abierta = openDrawers[u.email];
    if (!abierta) return closeCloseModal();
    const finalAmount = Number(closing.finalAmount);
    if (closing.finalAmount === '' || !Number.isSafeInteger(finalAmount) || finalAmount < 0 || finalAmount > 1000000000) {
      setClosing(current => ({ ...current, error: 'Ingresa el efectivo contado físicamente (entre $0 y $1.000.000.000).' }));
      return;
    }
    try {
      setClosing(current => ({ ...current, loading: true, error: '' }));
      // El backend registra la devolución a la caja responsable dentro de la
      // misma transacción del cierre. No crear internalTransfers aquí: hacerlo
      // desde el navegador podía dejar una devolución huérfana si luego fallaba
      // el cierre y duplicarla al reintentar.
      const closedCash = await closeCashRegister(abierta.id, {
        finalAmount,
        observations: `Cierre administrativo desde Turnos de Caja por ${currentUser?.email || 'admin'}`,
      }, { administrativeClose: true });

  setClosing({ open:false, uid:null, email:'', loading:false, error:'', finalAmount:'' });

  // ✅ toast bonito
  showToast({
    type: 'success',
    title: 'Turno cerrado',
    message: `${u.displayName || u.email} — Contado: ${fmtCOP(closedCash.finalAmount ?? finalAmount)}. ${closedCash.status || 'Cerrado'}; diferencia: ${fmtCOP(closedCash.difference ?? 0)}`,
    autoCloseMs: 3800,
  });
} catch (e) {
  console.error(e);
  setClosing(current => ({
    ...current,
    loading: false,
    error: e?.message || 'No se pudo cerrar el turno. Intenta nuevamente.',
  }));
  showToast({
    type: 'error',
    title: 'No se pudo cerrar el turno',
    message: e?.message || 'Intenta nuevamente o revisa la conexión.',
    autoCloseMs: 5000,
  });
}
};


  // --- 1) Suscripción en tiempo real: usuarios
  useEffect(() => {
    const unsubUsers = onSnapshot(
      query(collection(db, 'users'), orderBy('email')),
      (snap) => {
        const arr = snap.docs.map(d => ({ id: d.id, uid: d.id, ...d.data() }));
        const filtered = arr.filter(u => u.isActive !== false);
        setUsers(filtered);
        setLoading(false);
      },
      (err) => {
        console.error('users snapshot:', err);
        setLoading(false);
      }
    );

    // --- 2) Suscripción en tiempo real: turnos abiertos
    const unsubDrawers = onSnapshot(
      query(collection(db, 'cajas'), where('estado', '==', 'abierta'), orderBy('openedAt', 'desc')),
      (snap) => {
        const map = {};
        const rows = [];
        for (const d of snap.docs) {
          const data = d.data();
          rows.push({ id: d.id, ...data });
          const email = data.user;
          if (!map[email]) map[email] = { id: d.id, ...data };
        }
        setOpenDrawers(map);
        setOpenDrawerRows(rows);
      },
      (err) => console.error('cajas snapshot:', err)
    );

    return () => { unsubUsers(); unsubDrawers(); };
  }, []);

  // --- 3) Métricas por turno (ventas / abonos / retiros) — se crean suscripciones dinámicas por turno abierto
  const salesUnsubsRef = useRef([]);
  useEffect(() => {
    // Limpia subs anteriores
    salesUnsubsRef.current.forEach(u => u && u());
    salesUnsubsRef.current = [];

    const unsubs = [];
    gastoStoreRef.current = { turn: {}, related: {} };

    openDrawerRows.forEach(drawer => {
      const turnId = drawer.id;

      // Ventas por turno (para totales y últimas)
      const qV = query(collection(db, 'ventas'), where('turnId', '==', turnId), orderBy('createdAt', 'desc'));
      const unsubV = onSnapshot(qV, (snap) => {
 let salesCount = 0, salesTotal = 0, cashIn = 0, change = 0;
 const sales = [];
 const latest = [];
 const methodTotals = {}; // ← nuevo
 const addMethod = (label, amount) => {
  if (!label || !amount) return;
  const k = String(label).toLowerCase();
 const norm = k.includes('efect') ? 'Efectivo'
           : k.includes('tarj') ? 'Tarjeta'
           : (k.includes('transf') || k.includes('nequi') || k.includes('daviplata')) ? 'Transferencia'
           : (label || 'Otro');
   methodTotals[norm] = (methodTotals[norm] || 0) + Number(amount||0);
};
 snap.forEach((docv, idx) => {          const v = docv.data();
          sales.push(v);
          salesCount += 1;
          salesTotal += Number(v.total || 0);

          const pm1 = v.paymentMethod || '';
          const pm2 = v.secondPaymentMethod || '';
          const a1 = Number(v.amountReceived || 0);
          const a2 = Number(v.secondAmountReceived || 0);
 const cashReceived =
  (pm1.toLowerCase().includes('efect') ? a1 : 0) +
   (pm2.toLowerCase().includes('efect') ? a2 : 0);          cashIn += cashReceived;
          change += Number(v.change || 0);
 addMethod(pm1, a1);
 addMethod(pm2, a2);
          if (idx < 5) {
            latest.push({
              id: docv.id,
              invoiceNumber: safeDisplayText(v.invoiceNumber),
              total: Number(v.total || 0),
              ts: v.createdAt?.toDate?.() ? v.createdAt.toDate() : null
            });
          }
        });

        const accounting = calculateTurnSalesAccounting(sales);
        const canonicalMethodTotals = Object.fromEntries(
          Object.entries(accounting.salesByMethod).map(([method, value]) => [method, Number(value.amount || 0)])
        );
        const canonicalSalesTotal = Object.values(accounting.salesByMethod)
          .reduce((sum, value) => sum + Number(value.amount || 0), 0);

        setMetrics(prev => ({
  ...prev, [turnId]: {
    ...(prev[turnId] || {}),
    salesCount,
    salesTotal: canonicalSalesTotal,
    cashIn: accounting.efectivoRecibido,
    change: accounting.cambioEntregado,
    cashRefunds: accounting.cashRefunds,
    lastSales: latest,
    methodTotals: canonicalMethodTotals,
  }
        }));
      });
      unsubs.push(unsubV);

      // Abonos (ingresos) que afectan caja
      const qI = query(collection(db, 'ingresos'), where('turnId', '==', turnId), where('affectsCash', '==', true));
      const unsubI = onSnapshot(qI, (snap) => {
        let manualIngresos = 0;
        snap.forEach(di => {
          const d = di.data();
          manualIngresos += Number(d.totalEntryCost ?? d.amount ?? 0);
        });
        setMetrics(prev => {
          const current = prev[turnId] || {};
          const manualAdvanceTotal = Number(current.manualAdvanceTotal || 0);
          return {
            ...prev,
            [turnId]: {
              ...current,
              manualIngresos,
              abonos: manualIngresos + manualAdvanceTotal,
            }
          };
        });
      });
      unsubs.push(unsubI);

      const qE = query(
        collection(db, 'gastos'),
        where('turnId', '==', turnId),
        where('affectsCash', '==', true)
      );
      const unsubE = onSnapshot(qE, (snap) => {
        const expenseTotal = snap.docs.reduce(
          (sum, expense) => sum + Number(expense.data().amount || 0),
          0
        );
        setMetrics(prev => ({
          ...prev,
          [turnId]: { ...(prev[turnId] || {}), expenseTotal },
        }));
      });
      unsubs.push(unsubE);

      // Retiros (gastos) que afectan caja (turnId + relatedTurnId)
      const updateGastosMetrics = (key, docs) => {
        gastoStoreRef.current[key][turnId] = docs;
        const turnDocs = gastoStoreRef.current.turn[turnId] || [];
        const relatedDocs = gastoStoreRef.current.related[turnId] || [];
        const map = new Map();
        [...turnDocs, ...relatedDocs].forEach(doc => {
          map.set(doc.id, doc);
        });
        const merged = Array.from(map.values());
        const movements = merged.map(doc => {
          const amount = Number(doc.amount ?? doc.totalEntryCost ?? 0);
          const direction = doc.toTurnId === turnId ? 'in' : 'out';
          const isAdvanceKind = doc.type === 'advance';
          const desc = (doc.description || '').toString().toLowerCase();
          const isBaseAssignment = desc.includes('base para');
          return {
            id: doc.id,
            amount,
            type: doc.type,
            direction,
            fromTurnId: doc.fromTurnId || null,
            toTurnId: doc.toTurnId || null,
            category: safeDisplayText(doc.category) || safeDisplayText(doc.entryType) || 'Movimiento interno',
            description: safeDisplayText(doc.description) || safeDisplayText(doc.note),
            relatedUser: safeDisplayText(doc.relatedUser) || safeDisplayText(doc.user) || safeDisplayText(doc.source),
            isAdvance: isAdvanceKind && direction === 'in',
            isBaseAssignment,
          };
        });
        const internalIn = movements.reduce((acc, item) => acc + (item.direction === 'in' ? item.amount : 0), 0);
        const internalOut = movements.reduce((acc, item) => acc + (item.direction === 'out' ? item.amount : 0), 0);
        const advanceTotal = movements
          .filter(item => item.type === 'advance' && item.direction === 'in')
          .reduce((acc, item) => acc + item.amount, 0);
        const returnTotal = movements
          .filter(item => item.type === 'return' && item.direction === 'in')
          .reduce((acc, item) => acc + item.amount, 0);
        console.log("internalTransfers metrics", turnId, movements);
        setMetrics(prev => {
          const current = prev[turnId] || {};
          const manualIngresos = Number(current.manualIngresos || 0);
          const manualAdvanceTotal = movements
            .filter(item => item.type === 'advance' && item.direction === 'in' && !item.isBaseAssignment)
            .reduce((sum, item) => sum + Number(item.amount || 0), 0);
          return {
            ...prev,
            [turnId]: {
              ...current,
              internalIn,
              retiros: internalOut,
              movements,
              advanceTotal,
              manualAdvanceTotal,
              returnTotal,
              abonos: manualIngresos + manualAdvanceTotal,
            },
          };
        });
      };
      const qG = query(
        collection(db, 'internalTransfers'),
        where('fromTurnId', '==', turnId),
        where('affectsCash', '==', true)
      );
      const unsubG = onSnapshot(qG, (snap) => {
        const docs = snap.docs.map(dg => ({ id: dg.id, ...dg.data() }));
        if (!gastoStoreRef.current.turn[turnId]) gastoStoreRef.current.turn[turnId] = [];
        updateGastosMetrics('turn', docs);
      });
      unsubs.push(unsubG);
      const qGRelated = query(
        collection(db, 'internalTransfers'),
        where('toTurnId', '==', turnId),
        where('affectsCash', '==', true)
      );
      const unsubGRelated = onSnapshot(qGRelated, (snap) => {
        const docs = snap.docs.map(dg => ({ id: dg.id, ...dg.data() }));
        if (!gastoStoreRef.current.related[turnId]) gastoStoreRef.current.related[turnId] = [];
        updateGastosMetrics('related', docs);
      });
      unsubs.push(unsubGRelated);
    });

    salesUnsubsRef.current = unsubs;
    return () => { unsubs.forEach(u => u && u()); };
  }, [openDrawerRows]);

useEffect(() => {
  if (viewMode !== 'historial' || !selectedWaiter?.email) {
    setHistory([]); setLastClosed(null);
    return;
  }

  const qHist = query(
    collection(db, 'cajas'),
    where('user', '==', selectedWaiter.email),
    where('estado', '==', 'cerrada'),
    orderBy('closedAt', 'desc')
  );

  const unsub = onSnapshot(qHist, (snap) => {
    const arr = snap.docs.map(d => ({ id: d.id, ...d.data() }));
    setHistory(arr.slice(0, 10)); // últimos 10
    setLastClosed(arr[0] || null);
  }, (err)=>console.error('historial cajas:', err));

  return () => unsub();
}, [viewMode, selectedWaiter]);

 // 🚨 Restricción de acceso según rol
  if (!['Administrador','Admin','TI'].includes(currentUser?.role)) {
    return (
      <div className="p-6">
        <button onClick={onBack} className="px-4 py-2 rounded-lg border">← Volver</button>
        <div className="mt-6 p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800">
          No tienes permisos para administrar turnos de caja.
        </div>
      </div>
    );
  }

// Meseros disponibles para abrir turno + cualquier usuario que ya tenga uno abierto.
// Un turno abierto nunca debe desaparecer de la tabla por el rol de su propietario.
const filteredUsers = useMemo(() => {
  const q = search.trim().toLowerCase();
  const baseUsers = users.filter(u => isWaiterRole(u.role));
  const merged = [...baseUsers];

  Object.keys(openDrawers).forEach(email => {
    if (!merged.find(u => u.email === email)) {
      const existing = users.find(u => u.email === email);
      const drawer = openDrawers[email];
      merged.push(existing || {
        id: drawer?.userUid || email,
        uid: drawer?.userUid || email,
        email,
        displayName: drawer?.userName || drawer?.displayName || email,
        role: drawer?.role || 'Usuario con turno abierto',
      });
    }
  });

  if (!q) return merged;
  return merged.filter(u =>
    (u.displayName || '').toLowerCase().includes(q) ||
    (u.email || '').toLowerCase().includes(q)
  );
}, [users, search, openDrawers]);

  // --- Acciones ---
const handleOpenFor = async (u) => {
  if (openDrawers[u.email]) {
    showToast({
      type: 'error',
      title: 'Turno existente',
      message: `Este usuario ya tiene un turno abierto (${openDrawers[u.email].fecha || ''}).`,
    });
    return;
  }

  if (!assignedDrawer) {
    showToast({
      type: 'error',
      title: 'Caja requerida',
      message: 'No hay ninguna caja abierta disponible para asignar base.',
    });
    return;
  }

  const base = Number(openAmount[u.uid] ?? 100000);
  if (!base || base <= 0) {
    showToast({
      type: 'error',
      title: 'Base inválida',
      message: 'Ingresa un monto mayor que cero para la base del mesero.',
    });
    return;
  }

  if (base > assignedDrawerAvailableCash) {
    showToast({
      type: 'error',
      title: 'Fondos insuficientes',
      message: 'Tu caja actual no tiene suficiente efectivo para asignar esa base.',
    });
    return;
  }

  setOpeningUser(u.uid);
  try {
    await openAssignedCashRegister({
      targetUid: u.uid,
      sourceCashId: assignedDrawer.id,
      amount: base,
    });

    showToast({
      type: 'success',
      title: 'Turno abierto',
      message: `${u.displayName || u.email} - Base: ${fmtCOP(base)} asignada desde ${currentUser?.displayName || currentUser?.email || 'admin'}`,
      autoCloseMs: 3200,
    });
  } catch (e) {
    console.error(e);
    showToast({
      type: 'error',
      title: 'No se pudo abrir el turno',
    message: e?.message || 'No se pudo completar la apertura segura del turno.',
      autoCloseMs: 3500,
    });
  } finally {
    setOpeningUser(null);
  }
};
  const openAdministrativeCloseModal = (drawer) => {
    if (!canAdministrativelyClose || !drawer?.id || adminClosingId) return;
    const drawerMetrics = metrics[drawer.id] || {};
    const estimatedCash = Math.round(
      Number(drawer.montoApertura || drawer.amount || 0)
      + Number(drawerMetrics.abonos || 0)
      + Number(drawerMetrics.returnTotal || 0)
      + Number(drawerMetrics.cashIn || 0)
      - Number(drawerMetrics.change || 0)
      - Number(drawerMetrics.cashRefunds || 0)
      - Number(drawerMetrics.retiros || 0)
      - Number(drawerMetrics.expenseTotal || 0)
    );
    const owner = users.find(user => user.uid === drawer.userUid || user.email === drawer.user);
    const ownerLabel = owner?.displayName || drawer.user || drawer.openedByEmail || drawer.id;
    setAdminCloseModal({
      open: true,
      drawer,
      ownerLabel,
      ownerEmail: drawer.user || owner?.email || drawer.openedByEmail || '',
      finalAmount: '',
      reason: '',
      estimatedCash,
      baseAmount: Number(drawer.montoApertura || drawer.amount || 0),
      salesTotal: Number(drawerMetrics.salesTotal || 0),
      error: '',
    });
  };

  const closeAdministrativeCloseModal = () => {
    if (adminClosingId) return;
    setAdminCloseModal((current) => ({ ...current, open: false, error: '' }));
  };

  const handleAdministrativeClose = async (event) => {
    event?.preventDefault?.();
    const { drawer, ownerLabel, finalAmount: amountInput, reason } = adminCloseModal;
    if (!canAdministrativelyClose || !drawer?.id || adminClosingId) return;
    const normalizedAmount = String(amountInput || '').replace(/[^0-9]/g, '');
    const finalAmount = Number(normalizedAmount);
    if (!normalizedAmount || !Number.isSafeInteger(finalAmount) || finalAmount < 0 || finalAmount > 1000000000) {
      setAdminCloseModal((current) => ({ ...current, error: 'Ingresa un valor de efectivo válido.' }));
      return;
    }
    if (!String(reason).trim()) {
      setAdminCloseModal((current) => ({ ...current, error: 'Debes registrar por qué se cierra este turno.' }));
      return;
    }

    setAdminClosingId(drawer.id);
    setAdminCloseModal((current) => ({ ...current, error: '' }));
    try {
      const closedCash = await closeCashRegister(drawer.id, {
        finalAmount,
        observations: `Cierre administrativo: ${String(reason).trim()}`,
      }, { administrativeClose: true });
      setAdminCloseModal((current) => ({ ...current, open: false, error: '' }));
      showToast({
        type: 'success',
        title: 'Turno cerrado administrativamente',
        message: `${ownerLabel} - Contado: ${fmtCOP(finalAmount)}. ${closedCash.status || 'Cerrado'}; diferencia: ${fmtCOP(closedCash.difference ?? 0)}`,
        autoCloseMs: 4000,
      });
    } catch (error) {
      console.error(error);
      setAdminCloseModal((current) => ({
        ...current,
        error: error?.message || 'No se pudo cerrar el turno. Revisa la información e intenta nuevamente.',
      }));
    } finally {
      setAdminClosingId(null);
    }
  };

  const summary = useMemo(() => {
    const entries = openDrawerRows;
    const totals = {
      openCount: entries.length,
      totalBase: 0,
      totalSales: 0,
      cashOnHand: 0,
      advanceTotal: 0,
      returnTotal: 0,
    };

    entries.forEach((drawer) => {
      const m = metrics[drawer.id] || {};
      const base = Number(drawer.montoApertura || 0);
      const totalAbonos = Number(m.abonos || 0);
      const netCash = base
        + totalAbonos
        + Number(m.returnTotal || 0)
        + (Number(m.cashIn || 0) - Number(m.change || 0) - Number(m.cashRefunds || 0))
        - Number(m.retiros || 0)
        - Number(m.expenseTotal || 0);

      totals.totalBase += base;
      totals.totalSales += Number(m.salesTotal || 0);
      totals.cashOnHand += netCash;
      totals.advanceTotal += Number(m.advanceTotal || 0);
      totals.returnTotal += Number(m.returnTotal || 0);
    });

    return totals;
  }, [openDrawerRows, metrics]);

  const openAdjustModal = (u, type) => {
    setAdjust({ open:true, type, uid:u.uid, email:u.email, value:'', note:'' });
  };

  const confirmAdjust = async () => {
    const u = users.find(x => x.uid === adjust.uid);
    if (!u) return setAdjust({ open:false, type:'abono', uid:null, email:'', value:'', note:'' });

    const abierta = openDrawers[u.email];
    if (!abierta) {
      alert('Este usuario no tiene turno abierto.');
      return;
    }

    const valor = Number(String(adjust.value).replace(/\D/g, '')) || 0;
    if (valor <= 0) return alert('Monto inválido');

    try {
      setAdjustLoading(true);
      if (adjust.type === 'abono') {
        await addDoc(collection(db, 'internalTransfers'), {
          createdAt: serverTimestamp(),
          fromTurnId: assignedDrawer?.id || null,
          toTurnId: abierta.id,
          amount: valor,
          type: 'advance',
          description: adjust.note || 'Abono de base',
          relatedUser: u.email,
          affectsCash: true,
          registeredBy: currentUser?.email || 'admin',
        });
      } else {
        await addDoc(collection(db, 'internalTransfers'), {
          createdAt: serverTimestamp(),
          fromTurnId: abierta.id,
          toTurnId: assignedDrawer?.id || null,
          amount: valor,
          type: 'return',
          description: adjust.note || '',
          relatedUser: u.email,
          affectsCash: true,
          registeredBy: currentUser?.email || 'admin',
        });
      }
      setAdjust({ open:false, type:'abono', uid:null, email:'', value:'', note:'' });
    } catch (e) {
      console.error(e);
      alert('No se pudo registrar el ajuste.');
    }
    finally {
      setAdjustLoading(false);
    }
  };

  const getDisplayName = (email = '') => {
    const safeEmail = safeDisplayText(email);
    if (!safeEmail) return 'Sin usuario';
    const match = users.find((u) => u.email === safeEmail);
    return safeDisplayText(match?.displayName) || safeEmail;
  };


  // Datos precalculados para el modal de cierre
  const closingUser = closing.open
    ? (users.find((x) => x.uid === closing.uid) || {})
    : {};

  const closingRep = closingUser.email ? buildCloseReport(closingUser) : null;

  const advanceMovements =
    closingRep?.movements?.filter(
      (mov) => mov.type === 'advance' && mov.direction === 'in'
    ) || [];

  const totalAdvanceAssigned = advanceMovements.reduce(
    (acc, mov) => acc + mov.amount,
    0
  );

  const detailMovements =
    (closingRep?.movements || []).filter(
      (mov) => !(mov.type === 'advance' && mov.isBaseAssignment)
    );


  return (
    <>
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 px-3 pb-32 pt-4 sm:px-6 lg:px-10 lg:pb-6">
      <div className="mx-auto flex w-full max-w-xl flex-col gap-5 lg:max-w-6xl lg:gap-8">
        <div className="mb-6 flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <button
            onClick={onBack}
            className="flex items-center rounded-lg border border-blue-200 px-3 py-2 text-sm font-semibold text-blue-600 hover:border-blue-400 hover:text-blue-800"
          >
            ← Volver
          </button>
          <div className="text-center sm:text-right">
            <p className="text-xs uppercase tracking-widest text-blue-500">Monitor de cajas</p>
            <h1 className="text-2xl font-extrabold text-gray-900 sm:text-3xl">Turnos de Caja (Admin)</h1>
          </div>
        </div>

        <div className="relative z-10 mb-5 rounded-2xl border border-gray-200 bg-white/90 px-4 py-4 text-sm shadow-sm backdrop-blur lg:sticky lg:top-4 lg:px-8">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div className="rounded-2xl border border-blue-200 bg-blue-50/70 p-4 text-blue-900">
              <div className="text-xs uppercase font-semibold tracking-wide">Turnos abiertos</div>
              <div className="text-3xl font-extrabold lg:text-4xl">{summary.openCount}</div>
              <div className="text-xs text-gray-500">Activos en el sistema</div>
            </div>
            <div className="rounded-2xl border border-emerald-200 bg-emerald-50/70 p-4 text-emerald-900">
              <div className="text-xs uppercase font-semibold tracking-wide">Base total</div>
              <div className="text-3xl font-extrabold lg:text-4xl">{fmtCOP(summary.totalBase)}</div>
              <div className="text-xs text-gray-500">Suma de bases iniciales</div>
            </div>
            <div className="rounded-2xl border border-gray-200 bg-white p-4 text-slate-900 shadow">
              <div className="text-xs uppercase font-semibold tracking-wide">Ventas de turnos abiertos</div>
              <div className="text-3xl font-extrabold lg:text-4xl">{fmtCOP(summary.totalSales)}</div>
              <div className="text-xs text-gray-500">Total bruto desde cada apertura</div>
            </div>
            <div className="rounded-2xl border border-amber-200 bg-amber-50/70 p-4 text-amber-900">
              <div className="text-xs uppercase font-semibold tracking-wide">Caja en mano</div>
              <div className="text-3xl font-extrabold lg:text-4xl">{fmtCOP(summary.cashOnHand)}</div>
              <div className="text-xs text-gray-500">Caja neta estimada</div>
              <div className="text-xs text-gray-500">
                Avances: {fmtCOP(summary.advanceTotal)} · Retornos: {fmtCOP(summary.returnTotal)}
              </div>
            </div>
          </div>
        </div>

        {canAdministrativelyClose && (
          <section className="rounded-2xl border border-rose-200 bg-white p-4 shadow-sm lg:p-6">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-lg font-extrabold text-gray-900">Todos los turnos abiertos</h2>
                <p className="text-sm text-gray-500">
                  TI y Administrador pueden cerrar turnos bloqueados o abandonados. El registro se conserva en el historial.
                </p>
              </div>
              <span className="rounded-full bg-rose-50 px-3 py-1 text-sm font-bold text-rose-700">
                {openDrawerRows.length} abierto(s)
              </span>
            </div>

            <div className="mt-4 space-y-2">
              {openDrawerRows.map((drawer) => {
                const owner = users.find(user => user.uid === drawer.userUid || user.email === drawer.user);
                const label = owner?.displayName || drawer.user || drawer.openedByEmail || 'Usuario sin identificar';
                return (
                  <div key={drawer.id} className="flex flex-col gap-3 rounded-xl border border-gray-200 bg-gray-50 p-3 sm:flex-row sm:items-center sm:justify-between">
                    <div className="min-w-0">
                      <p className="truncate font-bold text-gray-900">{label}</p>
                      <p className="truncate text-xs text-gray-500">
                        {drawer.user || owner?.email || 'Sin correo'} · Turno {drawer.id.slice(0, 8)}
                      </p>
                      <p className="mt-1 text-xs text-gray-600">
                        Base: {fmtCOP(drawer.montoApertura || drawer.amount || 0)} · Apertura: {drawer.fecha || 'Sin fecha'} {drawer.hora || ''}
                      </p>
                    </div>
                    <button
                      type="button"
                      disabled={adminClosingId === drawer.id}
                      onClick={() => openAdministrativeCloseModal(drawer)}
                      className="shrink-0 rounded-xl bg-rose-700 px-4 py-2 text-sm font-bold text-white transition hover:bg-rose-800 disabled:cursor-wait disabled:opacity-60"
                    >
                      {adminClosingId === drawer.id ? 'Cerrando…' : 'Cerrar administrativamente'}
                    </button>
                  </div>
                );
              })}
              {openDrawerRows.length === 0 && (
                <p className="rounded-xl bg-emerald-50 p-3 text-sm font-semibold text-emerald-800">No hay turnos abiertos.</p>
              )}
            </div>
          </section>
        )}
      </div>

      <div className="mx-auto w-full max-w-xl lg:max-w-6xl">
        <div className="bg-white rounded-xl shadow p-4 lg:p-6 lg:overflow-hidden">
          {/* Tabs */}
          <div className="mb-4 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
            <div className="flex overflow-x-auto rounded-full border border-gray-200 bg-white p-1 text-sm">
      <button
        onClick={()=>setViewMode('activos')}
        className={`flex-1 rounded-full px-4 py-2 font-semibold ${viewMode==='activos'?'bg-gray-900 text-white':'text-gray-600'}`}
      >
        Activos
      </button>
      <button
        onClick={()=>setViewMode('historial')}
        className={`flex-1 rounded-full px-4 py-2 font-semibold ${viewMode==='historial'?'bg-gray-900 text-white':'text-gray-600'}`}
      >
        Historial
      </button>
    </div>

    {viewMode === 'activos' && (
      <div className="flex flex-col gap-3 rounded-2xl border border-gray-200 bg-white/90 px-4 py-4 text-sm shadow-sm backdrop-blur lg:flex-row lg:items-start lg:justify-between lg:border-0 lg:bg-transparent lg:px-0 lg:py-0 lg:shadow-none">
        <div className="flex-1 space-y-2 lg:space-y-0">
          <input
            value={search}
            onChange={(e)=>setSearch(e.target.value)}
            placeholder="Buscar usuario por nombre o email…"
            className="w-full rounded-lg border border-gray-200 px-3 py-2 focus:border-blue-500 focus:ring-2 focus:ring-blue-200 lg:w-72"
          />
          {['Administrador','TI'].includes(currentUser?.role) && (
            <div className="flex flex-col gap-1 text-xs text-gray-600 lg:text-sm">
              <label className="font-semibold text-gray-500">Caja responsable</label>
              <select
                value={responsibleDrawerEmail}
                onChange={(e) => setResponsibleDrawerEmail(e.target.value)}
                className="w-full rounded-lg border border-gray-200 px-3 py-1.5 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-200"
                disabled={openDrawerOptions.length === 0}
              >
                <option value="">Selecciona una caja abierta</option>
                {openDrawerOptions.map(opt => (
                  <option key={opt.email} value={opt.email}>
                    {opt.label} ({fmtCOP(opt.amount)})
                  </option>
                ))}
              </select>
              {openDrawerOptions.length === 0 && (
                <p className="text-rose-600">No hay cajas abiertas disponibles.</p>
              )}
            </div>
          )}
        </div>
        <div className="text-xs text-gray-500 lg:text-sm lg:ml-6">
          {summary.openCount} turno(s) abiertos
        </div>
      </div>
    )}
  </div>

  {/* Vista ACTIVOS */}
    {viewMode === 'activos' && (
    <>
      <div className="space-y-3 lg:hidden">
        {filteredUsers.map((u) => {
          const opened = openDrawers[u.email];
          const m = opened ? (metrics[opened.id] || {}) : {};
          const baseValor = Number(openAmount[u.uid] ?? 100000);
  const totalAbonos = Number(m.abonos || 0);
  const showAdvanceLine = Number(m.advanceTotal || 0) > Number(opened?.montoApertura || 0);

          return (
            <div key={u.uid} className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-base font-semibold text-gray-900">{u.displayName || '—'}</p>
                  <p className="text-xs text-gray-500">{u.email}</p>
                </div>
                <span className={`rounded-full px-3 py-1 text-xs font-semibold ${opened ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-600'}`}>
                  {opened ? 'Abierto' : 'Sin base'}
                </span>
              </div>
              <div className="mt-3 grid grid-cols-2 gap-3 text-xs text-gray-500">
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2">
                  <p className="font-semibold text-gray-700">Base</p>
                  {opened ? (
                    <p>{fmtCOP(opened.montoApertura || 0)}</p>
                  ) : (
                    <input
                      type="number"
                      className="mt-1 w-full rounded border px-2 py-1 text-sm"
                      value={baseValor}
                      onChange={(e) =>
                        setOpenAmount((prev) => ({ ...prev, [u.uid]: Number(e.target.value || 0) }))
                      }
                    />
                  )}
                </div>
                <div className="rounded-lg border border-dashed border-gray-200 bg-gray-50 p-2">
                  <p className="font-semibold text-gray-700">Caja actual</p>
                  {opened ? (
                    <p>
                      {fmtCOP(
                        Number(opened.montoApertura || 0) +
                          totalAbonos +
                          Number(m.returnTotal || 0) +
                          (Number(m.cashIn || 0) - Number(m.change || 0) - Number(m.cashRefunds || 0)) -
                          Number(m.retiros || 0) -
                          Number(m.expenseTotal || 0)
                      )}
                    </p>
                  ) : (
                    <p>—</p>
                  )}
                </div>
              </div>
              {opened && showAdvanceLine && (
                <div className="mt-2 text-xs text-gray-500">
                  Avances entregados: {fmtCOP(m.advanceTotal || 0)} · Dinero recuperado: {fmtCOP(m.returnTotal || 0)}
                </div>
              )}
              <div className="mt-3 flex flex-wrap gap-2 text-sm">
                {!opened ? (
                  <button
                    disabled={openingUser === u.uid}
                    onClick={() => handleOpenFor(u)}
                    className="flex-1 rounded-lg bg-emerald-600 px-3 py-2 font-semibold text-white disabled:opacity-50"
                  >
                    {openingUser === u.uid ? 'Abriendo…' : 'Abrir turno'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => openAdjustModal(u, 'abono')}
                      className="flex-1 rounded-lg bg-blue-600 px-3 py-2 font-semibold text-white"
                    >
                      Abonar
                    </button>
                    <button
                      onClick={() => openAdjustModal(u, 'retiro')}
                      className="flex-1 rounded-lg bg-amber-600 px-3 py-2 font-semibold text-white"
                    >
                      Retirar
                    </button>
                    <button
                      onClick={() => openCloseModal(u)}
                      className="w-full rounded-lg bg-rose-600 px-3 py-2 font-semibold text-white"
                    >
                      Reporte / Cerrar
                    </button>
                  </>
                )}
              </div>
            </div>
          );
        })}
      </div>

        <div className="hidden lg:block">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-gray-50">
      <tr>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Usuario</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Email</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Estado</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Base</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Caja (actual)</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Ventas</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Abierto el</th>
        <th className="px-4 py-2 text-left text-xs font-semibold text-gray-600">Acciones</th>
      </tr>
    </thead>

    <tbody className="bg-white divide-y divide-gray-200">
      {filteredUsers.map((u) => {
        const opened = openDrawers[u.email]; // turno abierto de ese mesero (si existe)
        const m = opened ? (metrics[opened.id] || {}) : {};
        const baseValor = Number(openAmount[u.uid] ?? 100000);
        const totalAbonos = Number(m.abonos || 0);
        const showAdvanceLine = Number(m.advanceTotal || 0) > Number(opened?.montoApertura || 0);

        return (
          <tr key={u.uid} className="hover:bg-gray-50">
            {/* Usuario */}
            <td className="px-4 py-2">
              <div className="font-medium text-gray-900">{u.displayName || "—"}</div>
              <div className="text-xs text-gray-500">{u.role || "Mesero"}</div>
            </td>
            {/* Email */}
            <td className="px-4 py-2 text-sm text-gray-600">{u.email}</td>

            {/* Estado */}
            <td className="px-4 py-2">
              {opened ? (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                  Abierto
                </span>
              ) : (
                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                  Sin base
                </span>
              )}
            </td>

            {/* Base (input cuando NO hay turno abierto) */}
            <td className="px-4 py-2">
              {opened ? (
                <div className="text-sm text-gray-700">{fmtCOP(opened.montoApertura || 0)}</div>
              ) : (
                <input
                  type="number"
                  className="w-28 border rounded px-2 py-1 text-sm"
                  value={baseValor}
                  onChange={(e) =>
                    setOpenAmount((prev) => ({ ...prev, [u.uid]: Number(e.target.value || 0) }))
                  }
                />
              )}
            </td>

            {/* Caja (actual) */}
            <td className="px-4 py-2 text-sm text-gray-700">
              {opened ? (
                <>
                  <div>En caja: {fmtCOP(
                    Number(opened.montoApertura || 0) +
                    totalAbonos +
                    Number(m.returnTotal || 0) +
                    (Number(m.cashIn || 0) - Number(m.change || 0) - Number(m.cashRefunds || 0)) -
                    Number(m.retiros || 0) -
                    Number(m.expenseTotal || 0)
                  )}</div>
                  <div className="text-xs text-gray-500">
                    Efectivo rec.: {fmtCOP(m.cashIn || 0)} · Cambio: {fmtCOP(m.change || 0)}
                  </div>
                  <div className="text-xs text-gray-500">
                    Abonos: {fmtCOP(totalAbonos)} · Retiros: {fmtCOP(m.retiros || 0)} · Gastos: {fmtCOP(m.expenseTotal || 0)}
                  </div>
                  {showAdvanceLine && (
                    <div className="text-xs text-gray-500">
                      Avances entregados: {fmtCOP(m.advanceTotal || 0)} · Dinero recuperado: {fmtCOP(m.returnTotal || 0)}
                    </div>
                  )}
                </>
              ) : (
                '—'
              )}
            </td>

            {/* Ventas */}
            <td className="px-4 py-2 text-sm text-gray-700">
              {opened ? (
                <>
                  <div># Ventas: {m.salesCount || 0}</div>
                  <div className="text-xs text-gray-500">Total: {fmtCOP(m.salesTotal || 0)}</div>
                </>
              ) : (
                '—'
              )}
            </td>

            {/* Abierto el */}
            <td className="px-4 py-2 text-sm text-gray-700">
              {opened ? (
                <>
                  <div>{opened.fecha || '—'}</div>
                  <div className="text-xs text-gray-500">{opened.hora || ''}</div>
                </>
              ) : (
                '—'
              )}
            </td>

            {/* Acciones */}
            <td className="px-4 py-2">
              <div className="flex flex-wrap gap-2">
                {!opened ? (
                  <button
                    disabled={openingUser === u.uid}
                    onClick={() => handleOpenFor(u)}
                    className="px-3 py-1.5 rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700 disabled:opacity-50"
                  >
                    {openingUser === u.uid ? 'Abriendo…' : 'Abrir turno'}
                  </button>
                ) : (
                  <>
                    <button
                      onClick={() => openAdjustModal(u, 'abono')}
                      className="px-3 py-1.5 rounded bg-blue-600 text-white text-sm hover:bg-blue-700"
                    >
                      Abonar
                    </button>
                    <button
                      onClick={() => openAdjustModal(u, 'retiro')}
                      className="px-3 py-1.5 rounded bg-amber-600 text-white text-sm hover:bg-amber-700"
                    >
                      Retirar
                    </button>
                    <button
                      onClick={() => openCloseModal(u)}
                      className="px-3 py-1.5 rounded bg-rose-600 text-white text-sm hover:bg-rose-700"
                    >
                      Reporte / Cerrar
                    </button>
                  </>
                )}
              </div>
            </td>
          </tr>
        );
      })}
    </tbody>
          </table>
        </div>
      </div>
    </>
  )}
  {/* Vista HISTORIAL */}
  {viewMode === 'historial' && (
    <div className="grid md:grid-cols-3 gap-4">
      {/* Columna izquierda: elegir mesero */}
      <div className="md:col-span-1">
        <div className="border rounded-lg p-3">
          <div className="font-semibold mb-2">Selecciona un mesero</div>
          <div className="space-y-1 max-h-[380px] overflow-auto">
            {users.filter(u=>isWaiterRole(u.role)).map(u=>(
              <button
                key={u.uid}
                onClick={()=>setSelectedWaiter(u)}
                className={`w-full text-left px-3 py-2 rounded ${selectedWaiter?.uid===u.uid?'bg-blue-50 border border-blue-200':'hover:bg-gray-50'}`}
              >
                <div className="font-medium">{u.displayName || u.email}</div>
                <div className="text-xs text-gray-500">{u.email}</div>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Columna central: último turno cerrado */}
      <div className="md:col-span-2 space-y-4">
        <div className="border rounded-lg p-4">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Último turno cerrado</h3>
            {lastClosed && (
              <div className="text-xs text-gray-500">
                Cerrado: {lastClosed.closedAt?.toDate?.() ? lastClosed.closedAt.toDate().toLocaleString() : '—'}
              </div>
            )}
          </div>

          {!lastClosed ? (
            <div className="text-gray-500 mt-2">Selecciona un mesero para ver su último turno cerrado.</div>
          ) : (
            <div className="mt-3 grid sm:grid-cols-2 gap-3 text-sm">
              <div className="space-y-1">
                <div><b>Fecha:</b> {lastClosed.fecha || '—'} {lastClosed.hora ? `· ${lastClosed.hora}` : ''}</div>
                <div><b>Base:</b> {fmtCOP(lastClosed.totals?.base ?? lastClosed.montoApertura ?? 0)}</div>
                <div><b>Abonos:</b> {fmtCOP(lastClosed.totals?.abonos ?? 0)}</div>
                <div><b>Retiros:</b> {fmtCOP(lastClosed.totals?.retiros ?? 0)}</div>
              </div>
              <div className="space-y-1">
                <div><b># Ventas:</b> {lastClosed.totals?.ventasCount ?? '—'}</div>
                <div><b>Total ventas:</b> {fmtCOP(lastClosed.totals?.ventasTotal ?? 0)}</div>
                <div><b>Efectivo rec.:</b> {fmtCOP(lastClosed.totals?.efectivoRecibido ?? 0)}</div>
                <div><b>Caja final:</b> {fmtCOP(lastClosed.totals?.cajaFinal ?? 0)}</div>
              </div>

              <div className="sm:col-span-2 pt-2 border-t">
                <div className="text-xs text-gray-600 mb-1">Recaudo por método</div>
                <div className="flex flex-wrap gap-2">
                  {Object.entries(lastClosed.totals?.methods || {}).map(([k,v])=>(
                    <span key={k} className="px-2 py-1 rounded bg-gray-100 text-gray-700 text-xs">
                      {k}: {fmtCOP(v)}
                    </span>
                  ))}
                </div>
              </div>

              <div className="sm:col-span-2 flex gap-2 justify-end">
                <button
                  onClick={()=>printCloseReport(
                    selectedWaiter,
                    {
                      turnId: lastClosed.id,
                      fecha: lastClosed.fecha,
                      hora: lastClosed.hora,
                      base: lastClosed.totals?.base ?? lastClosed.montoApertura ?? 0,
                      abonos: lastClosed.totals?.abonos ?? 0,
                      retiros: lastClosed.totals?.retiros ?? 0,
                      ventasCount: lastClosed.totals?.ventasCount ?? 0,
                      ventasTotal: lastClosed.totals?.ventasTotal ?? 0,
                      efectivoRecibido: lastClosed.totals?.efectivoRecibido ?? 0,
                      cambio: lastClosed.totals?.cambio ?? 0,
                      efectivoNeto: lastClosed.totals?.efectivoNeto ?? 0,
                      cajaFinal: lastClosed.totals?.cajaFinal ?? 0,
                      methods: lastClosed.totals?.methods ?? {},
                      lastSales: [], // opcional si no guardaste últimas ventas
                    }
                  )}
                  className="px-3 py-2 rounded bg-gray-800 text-white hover:bg-gray-900"
                >
                  Reimprimir
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Lista de últimos turnos */}
        <div className="border rounded-lg">
          <div className="p-3 font-semibold">Últimos turnos cerrados</div>
          <div className="divide-y">
            {history.map(h=>(
              <div key={h.id} className="p-3 flex items-center justify-between text-sm">
                <div>
                  <div className="font-medium">{h.fecha || '—'} {h.hora ? `· ${h.hora}` : ''}</div>
                  <div className="text-xs text-gray-500">Base {fmtCOP(h.totals?.base ?? h.montoApertura ?? 0)} · Ventas {fmtCOP(h.totals?.ventasTotal ?? 0)}</div>
                </div>
                <div className="flex gap-2">
                  <button
                    className="px-2 py-1 rounded border text-gray-700 hover:bg-gray-50"
                    onClick={()=>setLastClosed(h)}
                  >
                    Ver
                  </button>
                  <button
                    className="px-2 py-1 rounded bg-gray-800 text-white hover:bg-gray-900"
                    onClick={()=>printCloseReport(
                      selectedWaiter,
                      {
                        turnId: h.id,
                        fecha: h.fecha,
                        hora: h.hora,
                        base: h.totals?.base ?? h.montoApertura ?? 0,
                        abonos: h.totals?.abonos ?? 0,
                        retiros: h.totals?.retiros ?? 0,
                        ventasCount: h.totals?.ventasCount ?? 0,
                        ventasTotal: h.totals?.ventasTotal ?? 0,
                        efectivoRecibido: h.totals?.efectivoRecibido ?? 0,
                        cambio: h.totals?.cambio ?? 0,
                        efectivoNeto: h.totals?.efectivoNeto ?? 0,
                        cajaFinal: h.totals?.cajaFinal ?? 0,
                        methods: h.totals?.methods ?? {},
                        lastSales: [],
                      }
                    )}
                  >
                    Imprimir
                  </button>
                </div>
              </div>
            ))}
            {selectedWaiter && history.length === 0 && (
              <div className="p-3 text-sm text-gray-500">No hay turnos cerrados para este mesero.</div>
            )}
          </div>
        </div>
      </div>
    </div>
  )}
</div>
</div>
</div>

      {/* Modal de Abono/Retiro */}
      {adjust.open && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-4">
            <h3 className="text-lg font-semibold mb-2">
              {adjust.type === 'abono' ? 'Abonar a base' : 'Retirar de base'}
            </h3>
            <div className="space-y-2">
              <div>
                <label className="text-sm">Usuario</label>
                <div className="text-sm text-gray-700">{adjust.email}</div>
              </div>
              <div>
                <label className="text-sm">Valor</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  inputMode="numeric"
                  placeholder="Ej: 50000"
                  value={adjust.value}
                  onChange={(e)=>setAdjust(prev=>({...prev, value: e.target.value.replace(/\D/g,'')}))}
                />
              </div>
              <div>
                <label className="text-sm">Nota (opcional)</label>
                <input
                  className="w-full border rounded px-3 py-2"
                  value={adjust.note}
                  onChange={(e)=>setAdjust(prev=>({...prev, note: e.target.value}))}
                />
              </div>
            </div>
            <div className="mt-4 flex gap-2 justify-end">
              <button className="px-3 py-2 rounded border" onClick={()=>setAdjust({ open:false, type:'abono', uid:null, email:'', value:'', note:'' })}>
                Cancelar
              </button>
              <button
                className={`px-3 py-2 rounded text-white ${adjust.type==='abono'?'bg-blue-600 hover:bg-blue-700':'bg-amber-600 hover:bg-amber-700'}`}
                onClick={confirmAdjust}
                disabled={adjustLoading}
              >
                {adjustLoading ? 'Procesando...' : 'Confirmar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal de cierre */}
      {closing.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 px-3 py-6">
          <div className="flex h-full w-full max-w-5xl flex-col overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-2xl">
            <div className="border-b bg-white/90 px-6 py-4">
              <div className="text-lg font-semibold text-gray-900">
                Reporte de cierre · {safeDisplayText(closingUser.displayName) || safeDisplayText(closingUser.email) || 'Usuario'}
              </div>
              <div className="text-xs text-gray-500">
                {closingRep?.fecha ? `Fecha: ${closingRep.fecha}` : 'Fecha no disponible'}
                {closingRep?.hora ? ` · Hora: ${closingRep.hora}` : ''}
              </div>
            </div>

            <div className="flex-1 overflow-y-auto px-6 py-5 space-y-6">
              {!closingRep ? (
                <div className="text-center text-gray-500">Cargando...</div>
              ) : (
                <>
                  <div className="grid gap-4 md:grid-cols-2 text-sm text-gray-600">
                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-800">Base inicial</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.base)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Abonos</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.abonos)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Retiros</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.retiros)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Efectivo neto</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.efectivoNeto)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Gastos</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.gastos)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Efectivo esperado en caja</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.cajaFinal)}</span>
                      </div>
                    </div>

                    <div className="space-y-2">
                      <div className="flex justify-between">
                        <span className="font-medium text-gray-800">Ventas</span>
                        <span className="font-semibold text-gray-900">{closingRep.ventasCount}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Total ventas</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.ventasTotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Efectivo recibido</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.efectivoRecibido)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Cambio dado</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.cambio)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Avances asignados</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.advanceTotal)}</span>
                      </div>
                      <div className="flex justify-between">
                        <span>Dinero recuperado</span>
                        <span className="font-semibold text-gray-900">{fmtCOP(closingRep.returnTotal)}</span>
                      </div>
                    </div>
                  </div>

                  <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4 space-y-3">
                    <label htmlFor="close-counted-cash" className="block text-sm font-semibold text-gray-900">Efectivo contado físicamente (COP) *</label>
                    <p className="text-sm text-gray-600">Cuenta los billetes y monedas, incluida la base que quedó en caja. No incluyas transferencias ni pagos con tarjeta.</p>
                    <input
                      id="close-counted-cash"
                      type="text"
                      inputMode="numeric"
                      value={closing.finalAmount}
                      onChange={event => setClosing(current => ({ ...current, finalAmount: event.target.value.replace(/[^0-9]/g, ''), error: '' }))}
                      disabled={closing.loading}
                      placeholder="Ingresa el efectivo contado"
                      className="w-full rounded-xl border border-gray-300 bg-white px-4 py-3 text-lg"
                    />
                    {closing.finalAmount !== '' && (
                      <p role="status" className="text-sm font-semibold text-gray-900">
                        Contado: {fmtCOP(Number(closing.finalAmount))}.{' '}
                        {Number(closing.finalAmount) === closingRep.cajaFinal ? 'Sin diferencia' : Number(closing.finalAmount) > closingRep.cajaFinal ? 'Sobrante' : 'Faltante'}: {fmtCOP(Math.abs(Number(closing.finalAmount) - closingRep.cajaFinal))}
                      </p>
                    )}
                    <p className="text-xs text-gray-600">La diferencia es preliminar; al confirmar se recalcula con los movimientos del servidor. Si cierras desde otro equipo, verifica que el equipo de origen haya enviado todas sus operaciones.</p>
                  </div>

                  <div className="rounded-2xl border border-gray-200 bg-slate-50 p-4 text-sm text-gray-700">
                    <div className="flex items-center justify-between">
                      <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                        Recaudo por método
                      </span>
                      <span className="text-xs text-gray-500">
                        {Object.keys(closingRep.methods || {}).length} canal(es)
                      </span>
                    </div>
                    <div className="mt-3 flex flex-wrap gap-2 text-xs">
                      {Object.entries(closingRep.methods || {}).map(([k, v]) => (
                        <span
                          key={k}
                          className="rounded-full bg-white px-3 py-1 text-[11px] font-medium text-gray-700 shadow"
                        >
                          {k}: {fmtCOP(v)}
                        </span>
                      ))}
                      {!Object.keys(closingRep.methods || {}).length && (
                        <span className="text-xs text-gray-500">Sin movimientos</span>
                      )}
                    </div>
                  </div>

                  {closingRep.lastSales?.length > 0 && (
                    <div className="rounded-2xl border border-gray-200 bg-white p-4 text-xs text-gray-600">
                      <div className="flex items-center justify-between font-semibold text-gray-800">
                        <span>Últimas ventas</span>
                        <span>{closingRep.lastSales.length} registro(s)</span>
                      </div>
                      <div className="mt-2 space-y-1">
                        {closingRep.lastSales.map((sale) => (
                          <div key={sale.id} className="flex items-center justify-between">
                            <span>{safeDisplayText(sale.invoiceNumber) || safeDisplayText(sale.id).slice(0, 6) || 'Venta'}</span>
                            <span>{fmtCOP(sale.total)}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {detailMovements.length > 0 && (
                    <div className="rounded-2xl border border-dashed border-gray-200 bg-white p-4 text-sm text-gray-700">
                      <div className="flex items-center justify-between">
                        <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                          Movimientos de caja
                        </div>
                        <span className="text-xs text-gray-500">
                          {detailMovements.length} registro(s)
                        </span>
                      </div>

                      <div className="mt-3 grid grid-cols-2 gap-3 text-[11px] text-gray-600">
                        <div className="flex justify-between">
                          <span>Avances entregados</span>
                          <span className="font-semibold">{fmtCOP(closingRep.advanceTotal)}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Dinero recuperado</span>
                          <span className="font-semibold">{fmtCOP(closingRep.returnTotal)}</span>
                        </div>
                      </div>

                      {advanceMovements.length > 0 && (
                        <div className="mt-3 rounded-xl border border-gray-200 bg-slate-50 p-3 text-[11px] text-gray-600">
                          <div className="flex justify-between font-semibold text-gray-800">
                            <span>Base asignada al mesero</span>
                            <span>{fmtCOP(totalAdvanceAssigned)}</span>
                          </div>
                          <div className="mt-2 space-y-2">
                            {advanceMovements.map((mov) => (
                              <div key={`adv-${mov.id}`} className="flex justify-between">
                                <span>{getDisplayName(mov.relatedUser)}</span>
                                <span className="font-semibold">{fmtCOP(mov.amount)}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      <div className="max-h-[34vh] overflow-y-auto space-y-3 pr-2 pt-3">
                        {detailMovements.map((mov) => (
                          <div
                            key={mov.id}
                            className="flex justify-between gap-3 border-b border-gray-100 pb-2 last:border-0 last:pb-0"
                          >
                            <div className="flex-1">
                              <div className="text-sm font-medium text-gray-800">
                                {mov.category || 'Movimiento'}
                              </div>
                              {mov.description && (
                                <div className="text-xs text-gray-500">{mov.description}</div>
                              )}
                              {mov.relatedUser && (
                                <div className="text-[11px] text-gray-500">
                                  Responsable: {getDisplayName(mov.relatedUser)}
                                </div>
                              )}
                            </div>
                            <div className="text-right">
                              <div className="text-sm font-semibold">{fmtCOP(mov.amount)}</div>
                              <div className="text-[10px] uppercase tracking-widest text-gray-500">
                                {mov.isAdvance ? 'Avance' : 'Retiro'}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>

            {closing.error && (
              <div className="mx-6 mb-3 rounded-xl border border-rose-200 bg-rose-50 px-4 py-3 text-sm font-semibold text-rose-800">
                {safeDisplayText(closing.error, 'No se pudo cerrar el turno.')}
              </div>
            )}

            <div className="flex items-center justify-end gap-3 border-t px-6 py-4 bg-white/90">
              <button
                className="rounded-xl border px-4 py-2 text-sm font-medium"
                onClick={closeCloseModal}
                disabled={closing.loading}
              >
                Cancelar
              </button>
              {closingRep && (
                <>
                  <button
                    className="rounded-xl bg-gray-900 px-4 py-2 text-sm font-semibold text-white hover:bg-gray-800"
                    onClick={() => printCloseReport(closingUser, closingRep)}
                  >
                    Imprimir
                  </button>
                  <button
                    className="rounded-xl bg-rose-600 px-4 py-2 text-sm font-semibold text-white hover:bg-rose-700 disabled:cursor-wait disabled:opacity-60"
                    onClick={confirmCloseWithTotals}
                    disabled={closing.loading || closing.finalAmount === ''}
                  >
                    {closing.loading ? 'Cerrando…' : 'Confirmar cierre'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {adminCloseModal.open && (
        <div
          className="fixed inset-0 z-[70] flex items-center justify-center bg-slate-950/55 p-3 backdrop-blur-sm sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="admin-close-title"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) closeAdministrativeCloseModal();
          }}
        >
          <form
            onSubmit={handleAdministrativeClose}
            className="w-full max-w-xl overflow-hidden rounded-[28px] border border-white/70 bg-white shadow-2xl shadow-slate-950/30"
          >
            <div className="border-b border-slate-200 bg-gradient-to-r from-rose-50 via-white to-amber-50 px-5 py-5 sm:px-7">
              <div className="flex items-start gap-4">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-rose-700 text-xl font-black text-white shadow-lg shadow-rose-200">
                  !
                </div>
                <div className="min-w-0 flex-1">
                  <p className="text-xs font-bold uppercase tracking-[0.2em] text-rose-700">Acción administrativa</p>
                  <h2 id="admin-close-title" className="mt-1 text-xl font-black text-slate-950 sm:text-2xl">
                    Cerrar turno administrativamente
                  </h2>
                  <p className="mt-1 truncate text-sm text-slate-600">
                    {adminCloseModal.ownerLabel} {adminCloseModal.ownerEmail ? `· ${adminCloseModal.ownerEmail}` : ''}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={closeAdministrativeCloseModal}
                  disabled={Boolean(adminClosingId)}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-slate-200 bg-white text-xl text-slate-500 transition hover:bg-slate-100 disabled:opacity-40"
                  aria-label="Cerrar modal"
                >
                  ×
                </button>
              </div>
            </div>

            <div className="max-h-[70vh] space-y-5 overflow-y-auto px-5 py-5 sm:px-7 sm:py-6">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                <div className="rounded-2xl border border-slate-200 bg-slate-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-slate-500">Base inicial</p>
                  <p className="mt-1 text-lg font-black text-slate-900">{fmtCOP(adminCloseModal.baseAmount)}</p>
                </div>
                <div className="rounded-2xl border border-blue-200 bg-blue-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-blue-700">Ventas registradas</p>
                  <p className="mt-1 text-lg font-black text-blue-950">{fmtCOP(adminCloseModal.salesTotal)}</p>
                </div>
                <div className="rounded-2xl border border-amber-200 bg-amber-50 p-4">
                  <p className="text-[11px] font-bold uppercase tracking-wider text-amber-700">Caja estimada</p>
                  <p className="mt-1 text-lg font-black text-amber-950">{fmtCOP(adminCloseModal.estimatedCash)}</p>
                </div>
              </div>

              <div>
                <label htmlFor="admin-final-amount" className="block text-sm font-bold text-slate-800">
                  Efectivo contado <span className="text-rose-600">*</span>
                </label>
                <p className="mt-1 text-xs text-slate-500">Escribe el dinero físico que realmente quedó en esta caja, incluida la base. Si cierras desde otro equipo, verifica que el equipo de origen haya enviado todas sus operaciones.</p>
                <div className="mt-2 flex items-center rounded-2xl border border-slate-300 bg-white px-4 shadow-sm transition focus-within:border-rose-500 focus-within:ring-4 focus-within:ring-rose-100">
                  <span className="mr-2 text-lg font-black text-slate-500">$</span>
                  <input
                    id="admin-final-amount"
                    type="text"
                    inputMode="numeric"
                    autoFocus
                    value={adminCloseModal.finalAmount}
                    onChange={(event) => setAdminCloseModal((current) => ({
                      ...current,
                      finalAmount: event.target.value.replace(/[^0-9]/g, ''),
                      error: '',
                    }))}
                    className="min-w-0 flex-1 border-0 bg-transparent py-3 text-xl font-black text-slate-950 outline-none"
                    placeholder="0"
                    disabled={Boolean(adminClosingId)}
                  />
                  <span className="text-xs font-bold text-slate-400">COP</span>
                </div>
                <p className="mt-2 text-right text-sm font-bold text-slate-600">
                  {adminCloseModal.finalAmount === '' ? 'Pendiente de conteo' : `Contado: ${fmtCOP(Number(adminCloseModal.finalAmount))}. ${Number(adminCloseModal.finalAmount) === adminCloseModal.estimatedCash ? 'Sin diferencia' : Number(adminCloseModal.finalAmount) > adminCloseModal.estimatedCash ? 'Sobrante' : 'Faltante'}: ${fmtCOP(Math.abs(Number(adminCloseModal.finalAmount) - adminCloseModal.estimatedCash))} (preliminar)`}
                </p>
              </div>

              <div>
                <label htmlFor="admin-close-reason" className="block text-sm font-bold text-slate-800">
                  Motivo del cierre <span className="text-rose-600">*</span>
                </label>
                <textarea
                  id="admin-close-reason"
                  rows="3"
                  maxLength="450"
                  value={adminCloseModal.reason}
                  onChange={(event) => setAdminCloseModal((current) => ({
                    ...current,
                    reason: event.target.value,
                    error: '',
                  }))}
                  className="mt-2 w-full resize-none rounded-2xl border border-slate-300 px-4 py-3 text-sm outline-none transition focus:border-rose-500 focus:ring-4 focus:ring-rose-100"
                  placeholder="Ejemplo: turno abandonado, usuario no pudo realizar el cierre..."
                  disabled={Boolean(adminClosingId)}
                />
                <p className="mt-1 text-right text-xs text-slate-400">{adminCloseModal.reason.length}/450</p>
              </div>

              {adminCloseModal.error && (
                <div className="rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm font-semibold text-red-800" role="alert">
                  {adminCloseModal.error}
                </div>
              )}

              <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                <p className="font-bold">El cierre quedará registrado y auditado.</p>
                <p className="mt-1 text-xs text-amber-800">
                  Se guardará el efectivo contado, la diferencia, el motivo y el usuario que ejecutó la acción. No se eliminarán ventas ni movimientos.
                </p>
              </div>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-slate-200 bg-slate-50 px-5 py-4 sm:flex-row sm:justify-end sm:px-7">
              <button
                type="button"
                onClick={closeAdministrativeCloseModal}
                disabled={Boolean(adminClosingId)}
                className="rounded-2xl border border-slate-300 bg-white px-5 py-3 text-sm font-bold text-slate-700 transition hover:bg-slate-100 disabled:opacity-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={Boolean(adminClosingId)}
                className="rounded-2xl bg-rose-700 px-5 py-3 text-sm font-black text-white shadow-lg shadow-rose-200 transition hover:bg-rose-800 disabled:cursor-wait disabled:opacity-60"
              >
                {adminClosingId ? 'Cerrando turno…' : 'Confirmar cierre administrativo'}
              </button>
            </div>
          </form>
        </div>
      )}

 {toast.open && (
  <div className="fixed inset-0 bg-black/40 z-[60] flex items-center justify-center">
    <div className="bg-white w-full max-w-md mx-4 rounded-xl shadow-2xl border p-5">
      <div className="flex items-start gap-3">
        <div className={`mt-0.5 w-8 h-8 rounded-full flex items-center justify-center text-lg
          ${toast.type === 'success' ? 'bg-emerald-100 text-emerald-700' :
            toast.type === 'error' ? 'bg-rose-100 text-rose-700' : 'bg-gray-100 text-gray-700'}`}>
          {toast.type === 'success' ? '✓' : toast.type === 'error' ? '!' : 'i'}
        </div>
        <div className="flex-1">
          <div className="font-semibold text-gray-900">{toast.title}</div>
          {toast.message && <div className="text-sm text-gray-600 mt-1">{toast.message}</div>}
        </div>
      </div>

      <div className="mt-4 flex justify-end gap-2">
        <button
          className="px-3 py-2 rounded border"
          onClick={() => setToast(t => ({ ...t, open:false }))}
        >
          Aceptar
        </button>
      </div>
    </div>
  </div>
      )}
    </>
  );
}
